/**
 * Named Pattern Registry — canonical regex catalogue for the JSON form engine.
 *
 * ## Why this exists
 *
 * Across the Customer UI and Participant UI codebases, 100+ regex patterns are
 * scattered as raw strings in domain.json `pattern` fields, rules.json
 * `patternValue` fields, legacy `regExp.*` constants, and inline `.matches()`
 * calls. The same pattern (e.g. US ZIP 5 digits `^\d{5}$`) appears 3+ times
 * in a single rules.json, and dozens of times across the codebase.
 *
 * This registry provides a single authoritative source of truth for every
 * validated pattern. Once defined here it can be referenced by name:
 *
 *   - In `domain.json`: `"patternPreset": "ZIP_US"` (unconditional field-level)
 *   - In `rules.json`: `"patternPreset": "SSN_FORMAT"` (conditional rule)
 *
 * The engine resolves the preset name → { pattern, message } at load time.
 * Raw `pattern` / `patternValue` strings continue to work for one-off cases.
 *
 * ## How to add a new preset
 *
 * 1. Add an entry to `PATTERN_REGISTRY` below (or the appropriate sub-object).
 * 2. Use the preset in domain.json / rules.json by name.
 * 3. Write a unit test for the regex in the `__tests__/engine-contract/` folder.
 *
 * ## Naming conventions
 *
 * - SCREAMING_SNAKE_CASE
 * - Suffix `_US` for US-specific patterns (may be conditional on country flag)
 * - Suffix `_FORMAT` for fully-formatted strings (e.g. dashes inserted)
 * - Prefix `NO_` for negative / exclusion patterns
 *
 * @module patternRegistry
 */

export interface PatternDef {
  /** The regex to test against the field value. */
  pattern: RegExp;
  /** Default validation message. Supports `{title}` placeholder. */
  message: string;
  /**
   * True when this pattern should only be applied conditionally (e.g. US-only).
   * Informational — the engine does not auto-gate on this; use `condition` in rules.json.
   */
  conditional?: boolean;
  /** Human-readable description for authoring tools and documentation. */
  description?: string;
}

// ─── Digits & Numbers ─────────────────────────────────────────────────────────

const DIGITS: Record<string, PatternDef> = {
  /**
   * Digits only — no leading empty string allowed.
   * Covers US ZIP (step 1: is it all digits?), share counts, and general numeric fields.
   * @see ZIP_US_DIGITS for the conditional US-only version
   */
  DIGITS_ONLY: {
    pattern: /^[0-9]+$/,
    message: 'Must be only digits',
    description: 'One or more digits (0-9). No spaces, letters, or symbols.',
  },

  /** Digits and commas (for share counts like "1,000"). */
  DIGITS_WITH_COMMA: {
    pattern: /^[0-9,]*$/,
    message: 'Must be only digits',
    description: 'Digits and commas only. Used for formatted share counts.',
  },

  /** Whole number — no leading zeros, no decimal. Equivalent to legacy `regExp.digits`. */
  WHOLE_NUMBER: {
    pattern: /^[0-9][0-9]*$/,
    message: 'Must be only digits',
    description: 'One or more digits, first digit cannot be empty.',
  },

  /** Whole number including zero (0 is valid). Equivalent to legacy `regExp.negativeDigits`. */
  WHOLE_NUMBER_ALLOW_ZERO: {
    pattern: /^[0-9]*$/,
    message: 'Must be a whole number',
    description: 'Zero or more digits, allowing zero as a standalone value.',
  },

  /** Any whole number greater than zero. Equivalent to legacy `greaterThenZero`. */
  POSITIVE_INTEGER: {
    pattern: /^0*?[1-9]\d*$/,
    message: 'Must be greater than 0',
    description: 'Positive integer, no leading zeros.',
  },

  /** Decimal up to 4 places, optional thousands comma. Equivalent to legacy `regExp.decimal`. */
  DECIMAL_FOUR: {
    pattern: /^(\d+|\d{1,3}(,\d{3})*)(\.\d{0,4})?$/,
    message: 'Must be 4 decimal places only',
    description: 'Decimal number with optional thousands separator and up to 4 decimal places.',
  },

  /** Decimal up to 2 places, optional thousands comma. Equivalent to legacy `regExp.decimalTwo`. */
  DECIMAL_TWO: {
    pattern: /^(\d+|\d{1,3}(,\d{3})*)(\.\d{0,2})?$/,
    message: 'Must be 2 decimal places only',
    description: 'Decimal number with optional thousands separator and up to 2 decimal places.',
  },

  /** Loose decimal — digits with optional dot and comma, used for tax/financial inputs. */
  DECIMAL_LOOSE: {
    pattern: /^[0-9.,]*$/,
    message: 'Must be only digits',
    description: 'Digits, commas and dots allowed. Used for financial inputs.',
  },
};

// ─── Address & Geographic ─────────────────────────────────────────────────────

const ADDRESS: Record<string, PatternDef> = {
  /**
   * US ZIP code — exactly 5 digits.
   * Always pair with ZIP_US_DIGITS for a two-step validation
   * (first rule: digits only, second rule: exactly 5).
   * @conditional Apply only when country is United States (isOtherCountry !== true).
   */
  ZIP_US: {
    pattern: /^\d{5}$/,
    message: 'Must be 5 digits',
    conditional: true,
    description: 'Exactly 5 US ZIP code digits. Conditional on US country selection.',
  },

  /**
   * ZIP code first-pass — any number of digits only (no letters/spaces).
   * @conditional Apply only when country is United States.
   */
  ZIP_US_DIGITS: {
    pattern: /^[0-9]+$/,
    message: 'Must be only digits',
    conditional: true,
    description: 'Digits-only check for US ZIP. Applied before ZIP_US length check.',
  },

  /** Address line character set — alphanumeric plus common punctuation. */
  ADDRESS_LINE: {
    pattern: /^[a-zA-Z0-9 .,'\-]*$/,
    message: 'Invalid address',
    description: 'Address line characters: letters, digits, spaces, commas, dots, apostrophes, hyphens.',
  },

  /** Alphanumeric with dot (for shorter address texts). */
  ALPHANUMERIC_DOT: {
    pattern: /^[a-zA-Z0-9 .,]*$/,
    message: 'Must be alphanumeric only',
    description: 'Letters, digits, spaces, commas, and dots.',
  },

  /** City/state name — letters and spaces only (US domestic default). */
  CITY_NAME: {
    pattern: /^[a-zA-Z ]*$/,
    message: 'Invalid city or state name',
    description: 'Letters and spaces only. Used for US city/state text fields.',
  },
};

// ─── Phone & Contact ──────────────────────────────────────────────────────────

const PHONE: Record<string, PatternDef> = {
  /**
   * US phone number — formatted with dashes (NNN-NNN-NNNN) or raw 10 digits.
   * @conditional Apply only when country code is US (isOtherCountryPhone !== true).
   */
  PHONE_US: {
    pattern: /^(\d{10}|\d{3}-\d{3}-\d{4})$/,
    message: 'Must be 10 digits',
    conditional: true,
    description: 'US phone: 10 raw digits or NNN-NNN-NNNN format.',
  },

  /**
   * Numeric phone — digits and hyphens only. Used for general/international phones.
   * Equivalent to legacy `regExp.numericPhone`.
   */
  NUMERIC_PHONE: {
    pattern: /^[0-9\-]+$/,
    message: 'Must be only digits',
    description: 'Digits and hyphens only for phone number inputs.',
  },
};

// ─── Identity & Sensitive ─────────────────────────────────────────────────────

const IDENTITY: Record<string, PatternDef> = {
  /**
   * US SSN in formatted form — NNN-NN-NNNN.
   * @conditional Apply only when citizenship country is US (participantOtherCountry !== true).
   */
  SSN_FORMAT: {
    pattern: /^[0-9]{3}-[0-9]{2}-[0-9]{4}$/,
    message: 'Must be 9 digits',
    conditional: true,
    description: 'US SSN in NNN-NN-NNNN format. Conditional on US citizenship.',
  },

  /**
   * US Tax ID / EIN — formatted as NN-NNNNNNN.
   * @conditional Apply only when country is US.
   */
  EIN_FORMAT: {
    pattern: /^\d{2}-\d{7}$/,
    message: 'Must be XX-XXXXXXX format',
    conditional: true,
    description: 'US Employer Identification Number (EIN) in NN-NNNNNNN format.',
  },

  /**
   * Employee ID — alphanumeric plus underscore, hyphen, slash, space.
   * Matches the legacy Participant form `employeeId` validation.
   */
  EMPLOYEE_ID: {
    pattern: /^[a-zA-Z0-9_\-/ ]+$/,
    message: 'Must be alphanumeric or _, -, or /',
    description: 'Employee ID characters: letters, digits, underscore, hyphen, slash, space.',
  },

  /**
   * Username (login) — 4-100 chars, letters/digits/dots/hyphens or email format.
   * Matches legacy `USERNAME_REGEX` from commonValidation.
   */
  USERNAME: {
    pattern: /^(?=.{4,100}$)(?:[A-Za-z][A-Za-z0-9._\-]*[A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9.%+\-]*[A-Za-z0-9]@(?:[A-Za-z0-9](?:[A-Za-z0-9\-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,})$/,
    message: 'Invalid username',
    description: 'Username: 4-100 chars, letters/digits/dots/hyphens, or an email address.',
  },
};

// ─── Text & Name ──────────────────────────────────────────────────────────────

const TEXT: Record<string, PatternDef> = {
  /**
   * Name character set — letters, digits, and common punctuation.
   * Matches the standard person name charset used in Participant and system-user forms
   * across both Customer UI and Participant UI.
   * Equivalent to legacy `regExp.alphaNumWithBasicSymbols` / `charset: "nameChars"`.
   */
  NAME_CHARS: {
    pattern: /^[a-zA-Z0-9 @()\-/*"':;_#\-+]+$/,
    message: 'Invalid {title}',
    description: 'Name characters: letters, digits, spaces, and common symbols.',
  },

  /**
   * Person name with apostrophes and hyphens — used for legal/formal names.
   * Participant UI uses this for first/last/legal names.
   */
  NAME_WITH_APOSTROPHE: {
    pattern: /^[a-zA-Z\-'' ]*$/,
    message: 'Invalid {title}',
    description: 'Person name: letters, hyphens, apostrophes, and spaces.',
  },

  /**
   * Middle name with apostrophes and dot (for initials like "J.").
   * Participant UI middle name variant.
   */
  MIDDLE_NAME: {
    pattern: /^[a-zA-Z\-'' . ]*$/,
    message: 'Invalid middle name',
    description: 'Middle name: letters, hyphens, apostrophes, dots, and spaces.',
  },

  /** Alphanumeric only — letters and digits, no spaces. */
  ALPHANUMERIC: {
    pattern: /^[a-zA-Z0-9]+$/,
    message: 'Must be alphanumeric only',
    description: 'Letters and digits only. No spaces or symbols.',
  },

  /** Alphanumeric with spaces. */
  ALPHANUMERIC_SPACE: {
    pattern: /^[a-zA-Z0-9 ]*$/,
    message: 'Must be alphanumeric only',
    description: 'Letters, digits, and spaces.',
  },

  /** Alphanumeric with parentheses (for codes like "ABC(01)"). */
  ALPHANUMERIC_BRACKETS: {
    pattern: /^[a-zA-Z0-9()]+$/,
    message: 'Must be alphanumeric only',
    description: 'Letters, digits, and parentheses.',
  },

  /**
   * Alphanumeric with dash and underscore.
   * Used for identifiers that allow separators (signup step IDs, schema identifiers).
   */
  ALPHANUM_DASH_UNDERSCORE: {
    pattern: /^[a-zA-Z0-9\-_ ]+$/,
    message: 'Must be alphanumeric only',
    description: 'Letters, digits, hyphens, underscores, and spaces.',
  },

  /** Letters only — no digits or spaces. */
  LETTERS_ONLY: {
    pattern: /^[a-zA-Z]+$/,
    message: 'Must contain only letters',
    description: 'Alphabetic characters only.',
  },

  /** Letters and spaces. */
  LETTERS_AND_SPACES: {
    pattern: /^[A-Za-z\s]+$/,
    message: 'Must contain only letters and spaces',
    description: 'Alphabetic characters and whitespace.',
  },

  /** Text with hyphen and apostrophe only (strict name variant). */
  TEXT_WITH_HYPHEN: {
    pattern: /^[a-zA-Z\-'']+$/,
    message: 'Invalid {title}',
    description: 'Letters, hyphens, and apostrophes. No spaces or digits.',
  },
};

// ─── Email ───────────────────────────────────────────────────────────────────

const EMAIL: Record<string, PatternDef> = {
  /**
   * Email — case-insensitive standard format.
   * Prefer JSON Schema `"format": "email"` for domain fields.
   * Use this preset only in rules.json conditional email rules.
   */
  EMAIL: {
    pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
    message: 'Invalid email address',
    description: 'Standard email format (case-insensitive).',
  },
};

// ─── Financial / Securities ───────────────────────────────────────────────────

const FINANCIAL: Record<string, PatternDef> = {
  /**
   * Ticker symbol — 1-10 uppercase letters with optional exchange suffix.
   * Examples: "AAPL", "BRK-B", "BRK.A"
   */
  TICKER: {
    pattern: /^[A-Z]{1,10}([-\.][A-Z]{1,3})?$/,
    message: 'Invalid ticker',
    description: 'Equity ticker: 1-10 uppercase letters, optional .-suffix of 1-3 letters.',
  },
};

// ─── URL / Web ────────────────────────────────────────────────────────────────

const WEB: Record<string, PatternDef> = {
  /**
   * URL with required https?:// protocol.
   * Use for strict API endpoint / webhook URL inputs.
   */
  URL_WITH_PROTOCOL: {
    pattern: /^https?:\/\/(?:localhost(?::[0-9]{1,5})?|(?:[\w.-]+(?:\.[\w.\-]+)+))[\w\-._~:/?#[\]@!$&'()*+,;=.]*$/,
    message: 'Invalid URL',
    description: 'URL requiring http:// or https:// protocol.',
  },

  /**
   * URL — optional protocol, accepts bare domain URLs.
   * Use for user-facing website URL inputs.
   */
  URL: {
    pattern: /^(?:https?:\/\/)?(?:localhost(?::[0-9]{1,5})?|(?:[\w.-]+(?:\.[\w.\-]+)+))[\w\-._~:/?#[\]@!$&'()*+,;=.]*$/,
    message: 'Invalid website URL',
    description: 'URL with optional protocol prefix.',
  },

  /** Domain name — no www prefix, must have TLD. */
  DOMAIN_NAME: {
    pattern: /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/,
    message: 'Invalid domain name',
    description: 'Domain name: hostname.tld format, no protocol.',
  },
};

// ─── Password ─────────────────────────────────────────────────────────────────

const PASSWORD: Record<string, PatternDef> = {
  /**
   * Password complexity — min 8 chars, requires upper, lower, digit, and special char.
   * Matches both Customer UI and Participant UI password validation.
   */
  PASSWORD_COMPLEX: {
    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
    message: 'Password must be at least 8 characters and include uppercase, lowercase, number, and special character',
    description: 'Strong password: ≥8 chars, uppercase, lowercase, digit, special character.',
  },
};

// ─── Master registry ──────────────────────────────────────────────────────────

/**
 * The unified pattern registry.
 *
 * Access via `getPatternPreset(name)` in FormLoader and validationComposer.
 * All keys are stable public API — do NOT rename without updating all JSON schemas.
 */
export const PATTERN_REGISTRY: Record<string, PatternDef> = {
  // Digits
  ...DIGITS,
  // Address
  ...ADDRESS,
  // Phone
  ...PHONE,
  // Identity
  ...IDENTITY,
  // Text & Name
  ...TEXT,
  // Email
  ...EMAIL,
  // Financial
  ...FINANCIAL,
  // Web
  ...WEB,
  // Password
  ...PASSWORD,
};

/**
 * Look up a pattern preset by name.
 *
 * @example
 * ```typescript
 * const preset = getPatternPreset('ZIP_US');
 * // → { pattern: /^\d{5}$/, message: 'Must be 5 digits', conditional: true, ... }
 * ```
 */
export function getPatternPreset(name: string): PatternDef | undefined {
  return PATTERN_REGISTRY[name];
}

/**
 * List all registered pattern preset names.
 * Useful for authoring tools and schema validation.
 */
export function listPatternPresets(): string[] {
  return Object.keys(PATTERN_REGISTRY);
}
